import { Link, useParams } from 'react-router';

import { useDefinition } from '@/api';
import { Badge, Button, Card, CodeBlock, PageHeader, ProtocolBadge, Spinner } from '@/components/ui';
import { unitLabel } from '@/lib/format';

export function DefinitionPage() {
	const { id = '' } = useParams();
	const def = useDefinition(id);
	if (def.isError) return <p className="text-fg-muted">Unknown definition.</p>;
	if (!def.data) return <Spinner />;
	const d = def.data;
	return (
		<>
			<PageHeader
				title={d.name}
				subtitle={
					<>
						<code className="font-mono">{d.id}</code> · v{d.version} · {d.category}
					</>
				}
				actions={
					<Link to={`/instances/new?definition=${encodeURIComponent(d.id)}`}>
						<Button variant="primary">Create sensor</Button>
					</Link>
				}
			/>
			<p className="mb-4 max-w-3xl text-sm text-fg-muted">{d.description}</p>
			<div className="grid gap-4 lg:grid-cols-2">
				<Card title="Fields">
					<table className="w-full text-sm">
						<thead className="text-left text-xs text-fg-muted">
							<tr>
								<th className="pb-2 font-medium">Key</th>
								<th className="pb-2 font-medium">Label</th>
								<th className="pb-2 font-medium">Unit</th>
								<th className="pb-2 font-medium">Range</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-border">
							{d.fields.map((f) => (
								<tr key={f.key}>
									<td className="py-1.5 font-mono text-xs">{f.key}</td>
									<td className="py-1.5">{f.label}</td>
									<td className="py-1.5">{unitLabel(f.unit) || '—'}</td>
									<td className="py-1.5 text-xs text-fg-muted">{f.min !== null && f.min !== undefined ? `${f.min} … ${f.max}` : f.kind}</td>
								</tr>
							))}
						</tbody>
					</table>
				</Card>
				<Card title="Protocols">
					<p className="mb-3 text-sm">
						Expected: <ProtocolBadge protocol={d.expectedProtocol} />
					</p>
					<div className="flex flex-wrap gap-1">
						{d.supportedProtocols.map((p) => (
							<ProtocolBadge key={p} protocol={p} muted={p !== d.expectedProtocol} />
						))}
					</div>
				</Card>
				<Card title="Modbus register map">
					<table className="w-full text-sm">
						<thead className="text-left text-xs text-fg-muted">
							<tr>
								<th className="pb-2 font-medium">Offset</th>
								<th className="pb-2 font-medium">Field</th>
								<th className="pb-2 font-medium">Type</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-border">
							{d.registerMap.entries.map((e) => (
								<tr key={e.key}>
									<td className="py-1 font-mono text-xs">+{e.offset}</td>
									<td className="py-1 font-mono text-xs">{e.key}</td>
									<td className="py-1">
										<Badge>{e.encoding}</Badge>
									</td>
								</tr>
							))}
						</tbody>
					</table>
					<p className="mt-2 text-xs text-fg-muted">{d.registerMap.len} registers per sensor; status: 0 good, 1 uncertain, 2 bad.</p>
				</Card>
				<Card title="Payload (JSON)">
					<CodeBlock code={JSON.stringify(d.payloadExample, null, 2)} label="MQTT / CoAP / HTTP" />
				</Card>
			</div>
		</>
	);
}
